import React, { useState, useEffect, useContext } from 'react';
// import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/api';

// Helper function to get the correct API URL
// const getApiUrl = () => {
//   return process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'https://nivaas.app' : '');
// };

// Helper function to format creation date
/* const formatCreatedDate = (createdAt) => {
  if (!createdAt) {
    return 'Not available';
  }
  
  try {
    const date = new Date(createdAt);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    // Format with both date and time for better user experience
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting created date:', error);
    return 'Date formatting error';
  }
}; */

function formatCreatedDate(createdAt) {
  if (!createdAt || typeof createdAt !== 'string') return 'N/A';

  try {
    // Parse "YYYY-MM-DD HH:mm:ss" as UTC
    const [datePart, timePart] = createdAt.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute, second] = timePart.split(':').map(Number);

    // Create a UTC Date object
    const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

    // Format to IST using timeZone
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).format(utcDate);
  } catch (err) {
    console.error('Invalid date string:', createdAt, err);
    return 'Invalid Date';
  }
}

const Profile = () => {
  const { currentUser, refreshUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    role: '',
    created_at: ''
  });

  useEffect(() => {
    if (currentUser) {
      setProfile({
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        created_at: currentUser.created_at
      });
    }
  }, [currentUser]);

  const validateForm = () => {
    const newErrors = {};

    // Validate name
    if (!profile.name || !profile.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Validate email
    if (!profile.email || !profile.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const toggleEdit = () => {
    setEditing(!editing);
    if (!editing) {
      // Reset to current values when entering edit mode
      setProfile({
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        created_at: currentUser.created_at
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await authAPI.updateProfile({
        name: profile.name.trim(),
        email: profile.email.trim()
      });

      if (response.data) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setEditing(false);
        if (refreshUser) {
          console.log('Refreshing user data after profile update');
          await refreshUser(); // Refresh user data in context if available
        }
      }
    } catch (error) {

      // Specific error message based on status code
      if (error.response?.status === 404) {
        setMessage({ type: 'danger', text: 'Profile update endpoint not found. Please contact support.' });
      } else if (error.response?.status === 403) {
        setMessage({ type: 'danger', text: 'Authentication error. Please log out and back in.' });
      } else if (error.response?.status === 400 && error.response.data?.message) {
        setMessage({ type: 'danger', text: error.response.data.message });
      } else {
        setMessage({
          type: 'danger',
          text: error.response?.data?.message || 'Failed to update profile. Please try again.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Please log in to view your profile.
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-md-8 offset-md-2">
          <div className="card shadow">
            <div className="card-header text-white">
              <h3 className="mb-0">
                <i className="fas fa-user-circle me-2"></i>
                My Profile
              </h3>
            </div>
            <div className="card-body">
              {message.text && (
                <div className={`alert alert-${message.type}`}>
                  {message.type === 'success' ? (
                    <i className="fas fa-check-circle me-2"></i>
                  ) : (
                    <i className="fas fa-exclamation-circle me-2"></i>
                  )}
                  {message.text}
                </div>
              )}

              {editing ? (
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">Name</label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      id="name"
                      name="name"
                      value={profile.name}
                      onChange={handleInputChange}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">Email</label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      id="email"
                      name="email"
                      value={profile.email}
                      onChange={handleInputChange}
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <input
                      type="text"
                      className="form-control"
                      value={profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                      readOnly
                    />
                    <small className="text-muted">Role cannot be changed.</small>
                  </div>
                  <div className="d-flex justify-content-end flex-wrap">
                    <button
                      type="button"
                      className="btn btn-secondary mb-2 me-2"
                      onClick={toggleEdit}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary mb-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div>
                  <div className="row mb-3">
                    <div className="col-md-4 fw-bold">Name:</div>
                    <div className="col-md-8">{profile.name}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4 fw-bold">Email:</div>
                    <div className="col-md-8">{profile.email}</div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4 fw-bold">Role:</div>
                    <div className="col-md-8">
                      <span className="badge bg-info">
                        {profile.role === "owner"
                          ? "Resident"
                          : profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4 fw-bold">Account Created:</div>
                    <div className="col-md-8">
                      {profile.created_at && formatCreatedDate(profile.created_at)}
                    </div>
                  </div>
                  <div className="d-flex justify-content-end flex-wrap">
                    <Link to="/change-password" className="btn btn-secondary mb-2 me-2">
                      <i className="fas fa-key me-1"></i> Change Password
                    </Link>
                    <button className="btn btn-primary mb-2" onClick={toggleEdit} style={{ marginBlockStart: '0' }}>
                      <i className="fas fa-edit me-1"></i> Edit Profile
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile; 